import axios from './axios'
import { registerAccountResetHook } from './accountSession'

// /api/v4/* is the gateway prefix for the workflows-v2 public API; its internal
// /api/v1/public/* mount is not routed at the edge (404s → permanent legacy fallback).
export const INSIGHTS_V2_BASE = '/api/v4/insights'
export const INSIGHTS_V3_BASE = '/api/v3/insights'

let enabledCache: Promise<boolean> | undefined

registerAccountResetHook(() => {
  enabledCache = undefined
})

/**
 * v2-insights rollout gate, resolved once per session and memoized so every
 * consumer (Insights page, dashboard + category widgets) shares one call and
 * routes to the same backend. Defaults to legacy (false) if the call fails.
 */
export function getInsightsV2Enabled(): Promise<boolean> {
  if (!enabledCache) {
    enabledCache = axios
      .get(`${INSIGHTS_V2_BASE}/enabled`)
      .then((res) => !!res.data?.enabled)
      .catch(() => false)
  }
  return enabledCache
}

export async function getInsightsBase(): Promise<string> {
  return (await getInsightsV2Enabled()) ? INSIGHTS_V2_BASE : INSIGHTS_V3_BASE
}

export interface InsightsSearchParams {
  term?: string
  /** Insight sub types (`InsightSubType` values). */
  types?: string[]
  /** CPI technical names/slugs (e.g. `cpi042`), not display names. */
  cpis?: string[]
  /** Severity sections, lower-cased. */
  sections?: string[]
  instanceIds?: string[]
  dateFrom?: string
  dateTo?: string
  sortBy?: string
  sortDirection?: 'ASC' | 'DESC'
}

/**
 * Builds the `/search` request body for whichever insights backend `basePath` points at.
 * v2 takes a nested filter contract ({ query_filter: { COLUMN: { operator: value } } }) and
 * ignores the legacy flat keys, so every caller must go through here — a hand-rolled flat body
 * sent to v2 silently returns unfiltered results.
 */
export function buildInsightsSearchBody(params: InsightsSearchParams, basePath: string): Record<string, unknown> {
  const {
    term = '',
    types = [],
    cpis = [],
    sections = [],
    instanceIds = [],
    dateFrom,
    dateTo,
    sortBy,
    sortDirection
  } = params

  if (basePath !== INSIGHTS_V2_BASE) {
    const body: Record<string, unknown> = { term }
    if (dateFrom) body.date_from = dateFrom
    if (dateTo) body.date_to = dateTo
    if (types.length) body.sub_types = types
    if (cpis.length) body.cpis = cpis
    if (sections.length) body.sections = sections
    if (instanceIds.length) body.instance_ids = instanceIds
    // v3's sort_by enum has no created_at.
    if (sortBy) body.sort_by = sortBy === 'created_at' ? 'insight_date' : sortBy
    if (sortDirection) body.sort_direction = sortDirection

    return body
  }

  // Omit unused filters — an unknown/empty key returns HTTP 400 on v2.
  const queryFilter: Record<string, unknown> = {}
  if (types.length) queryFilter.TYPE = { any_: types }
  if (cpis.length) queryFilter.CPI_SLUG = { any_: cpis }
  if (sections.length) queryFilter.SEGMENT_VALUE = { any_: sections }
  // The date window filters created_at — the date shown on the card.
  if (dateFrom || dateTo) {
    queryFilter.CREATED_AT = {
      ...(dateFrom && { gte_: dateFrom }),
      ...(dateTo && { lte_: dateTo })
    }
  }
  if (term) queryFilter.TERM = { contains_: term }
  if (instanceIds.length) queryFilter.INSTANCE_IDS = { json_array_overlap_: instanceIds }

  const body: Record<string, unknown> = {}
  if (Object.keys(queryFilter).length) body.query_filter = queryFilter
  if (sortBy) body.sort_by = sortBy
  if (sortDirection) body.sort_direction = sortDirection

  return body
}
